import { FormEvent, useCallback, useState } from 'react';
import { useEmailOtpLogin } from '../login/useEmailOtpLogin';
import { isPhoneOtpEnabled } from '../login/phoneOtpAuth';
import { usePhoneOtpLogin } from '../login/usePhoneOtpLogin';
import { useI18n } from '../i18n';
import styles from '../pages/AuthLoginPortal.module.css';

type OtpChannel = 'email' | 'phone';

export function AuthOtpSection() {
  const { t } = useI18n();
  const phoneOtpEnabled = isPhoneOtpEnabled();
  const [channel, setChannel] = useState<OtpChannel>(() =>
    isPhoneOtpEnabled() ? 'phone' : 'email',
  );
  const emailOtp = useEmailOtpLogin('private client');
  const phoneOtp = usePhoneOtpLogin('private client');

  const activeChannel = phoneOtpEnabled ? channel : 'email';
  const inCodeStep = activeChannel === 'email' ? emailOtp.step === 'code' : phoneOtp.step === 'code';

  const hint =
    activeChannel === 'email'
      ? inCodeStep && emailOtp.codeSent
        ? t('auth.portal.codeSent', { email: emailOtp.email })
        : t('auth.portal.emailHint')
      : inCodeStep && phoneOtp.codeSent
        ? t('auth.portal.phoneCodeSent', { phone: phoneOtp.displayPhone })
        : t('auth.portal.phoneOtpHint');

  const onSwitchChannel = useCallback(
    (next: OtpChannel) => {
      if (next === channel) {
        return;
      }
      emailOtp.onBackToEmail();
      phoneOtp.onBackToPhone();
      setChannel(next);
    },
    [channel, emailOtp, phoneOtp],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (activeChannel === 'email') {
      emailOtp.onSubmit(event);
      return;
    }
    phoneOtp.onSubmit(event);
  };

  const submitting = activeChannel === 'email' ? emailOtp.submitting : phoneOtp.submitting;
  const errorFriendly = activeChannel === 'email' ? emailOtp.errorFriendly : phoneOtp.errorFriendly;

  return (
    <section aria-labelledby="auth-otp-title">
      {phoneOtpEnabled ? (
        <div className={styles.otpChannelTabs} role="tablist" aria-label={t('auth.portal.title')}>
          <button
            type="button"
            role="tab"
            className={styles.otpChannelTab}
            aria-selected={activeChannel === 'phone'}
            onClick={() => onSwitchChannel('phone')}
          >
            {t('auth.portal.phoneOtpTitle')}
          </button>
          <button
            type="button"
            role="tab"
            className={styles.otpChannelTab}
            aria-selected={activeChannel === 'email'}
            onClick={() => onSwitchChannel('email')}
          >
            {t('auth.portal.emailOtpTitle')}
          </button>
        </div>
      ) : null}

      <p id="auth-otp-title" className={styles.hint}>
        {hint}
      </p>

      <form className={styles.otpForm} onSubmit={onSubmit}>
        {activeChannel === 'email' && !inCodeStep ? (
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel} htmlFor="portal-email">
              {t('auth.portal.emailLabel')}
            </label>
            <input
              id="portal-email"
              className={styles.textInput}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={emailOtp.email}
              disabled={emailOtp.submitting}
              placeholder={t('auth.portal.emailPlaceholder')}
              onChange={(event) => emailOtp.onChangeEmail(event.target.value)}
            />
          </div>
        ) : null}

        {activeChannel === 'phone' && !inCodeStep ? (
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel} htmlFor="portal-phone">
              {t('auth.portal.phoneOtpLabel')}
            </label>
            <div className={styles.phoneRow}>
              <span className={styles.phonePrefix} aria-hidden>
                +972
              </span>
              <input
                id="portal-phone"
                className={styles.phoneInputActive}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={phoneOtp.phone}
                disabled={phoneOtp.submitting}
                placeholder={t('auth.portal.phoneOtpPlaceholder')}
                onChange={(event) => phoneOtp.onChangePhone(event.target.value)}
              />
            </div>
          </div>
        ) : null}

        {inCodeStep ? (
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel} htmlFor="portal-code">
              {t('auth.portal.codeLabel')}
            </label>
            <input
              id="portal-code"
              className={styles.textInput}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={activeChannel === 'email' ? emailOtp.code : phoneOtp.code}
              disabled={submitting}
              placeholder={
                activeChannel === 'email'
                  ? t('auth.portal.codePlaceholder')
                  : t('auth.portal.smsCodePlaceholder')
              }
              onChange={(event) =>
                activeChannel === 'email'
                  ? emailOtp.onChangeCode(event.target.value)
                  : phoneOtp.onChangeCode(event.target.value)
              }
            />
            <button
              type="button"
              className={styles.changeEmailLink}
              onClick={activeChannel === 'email' ? emailOtp.onBackToEmail : phoneOtp.onBackToPhone}
            >
              {activeChannel === 'email' ? t('auth.portal.changeEmail') : t('auth.portal.changePhone')}
            </button>
          </div>
        ) : null}

        {errorFriendly ? (
          <div className={styles.failureBlock} role="alert">
            <p className={styles.friendlyError}>{errorFriendly}</p>
          </div>
        ) : null}

        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting
            ? t('common.submitting')
            : inCodeStep
              ? t('auth.portal.verifyCode')
              : t('auth.portal.sendCode')}
        </button>
      </form>
    </section>
  );
}

/** @deprecated Use AuthOtpSection */
export const AuthEmailOtpSection = AuthOtpSection;
